/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { IntlProvider } from 'react-intl';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from 'react-router-dom';
import WindowManager from './windowDecorators/WindowManager';
import { PATHS } from '../app/constants/paths';

import store from './store';
import breakpoints from './constants/breakpoints';
import ImportScript from './hooks/importScript';
import Action from './views/Action';
import Home from './views/Home';
import TestHome from './views/TestHome';
import Error from './views/Error';
import Auth from './views/Auth';
import Authorization from './views/Authorization';
import PhoneRegistration from './views/PhoneRegistration';
import TestResults from './views/TestResults';
import ProductCheckoutPage from './views/ProductCheckoutPage';
import MyHealth from './views/MyHealth';
import MyHealthProducts from './views/MyHealthProducts';
import MyHealthEmployeeHealth from './views/MyHealthEmployeeHealth';
import MyHealthCheckups from './views/MyHealthCheckups';
import MyHealthShop from './views/MyHealthShop';
import LearnMorePost from './views/LearnMorePost';
import ProductPage from './views/ProductPage';
import TreatmentPage from './views/TreatmentPage';
import ResetPassword from './views/ResetPassword';
import Playground from './views/Playground';
import AlwaysAccessibleRouteWrapper from './views/AlwaysAccessibleRouteWrapper';
import { AuthContextProvider, useAuthState } from './context/firebase';

import './utils/modernizr';
import '../scss/index.scss';

new WindowManager(breakpoints.formatted, 500);

const AlwaysAccessibleRoute = ({ element, areMessagesLoaded, ...props }) => {
  const { isAuthenticated, isLoading } = useAuthState();

  if (isLoading || !areMessagesLoaded) {
    return <div className='home__loader'></div>;
  }

  return (
    <AlwaysAccessibleRouteWrapper isAuthenticated={isAuthenticated} {...props}>
      {element}
    </AlwaysAccessibleRouteWrapper>
  );
};

const AuthenticatedRoute = ({ element, areMessagesLoaded, ...props }) => {
  const { isAuthenticated, isLoading } = useAuthState();

  if (isLoading || !areMessagesLoaded) {
    return <div className='home__loader'></div>;
  }

  if (!isAuthenticated) {
    window.location.href = `${process.env.REACT_APP_SECURE_APP_URL}${PATHS.AUTHORIZATION}`;
  }

  return isAuthenticated ? element : null;
};

// Routes that has to be redirected to secure url, for example /authrization
// Because when user login, we are setting items in localstorage and it has to be
// on same url origin
const SecureRoute = ({ element, areMessagesLoaded, ...props }) => {
  const { isLoading } = useAuthState();
  const origin = window.location.origin;
  const href = window.location.href;

  if (isLoading || !areMessagesLoaded) {
    return <div className='home__loader'></div>;
  }

  if (origin !== process.env.REACT_APP_SECURE_APP_URL) {
    window.location.href = href.replace(
      origin,
      process.env.REACT_APP_SECURE_APP_URL,
    );
    return null;
  } else {
    return element;
  }
};

function flattenJSONMessages(nestedMessages, prefix = '') {
  return Object.keys(nestedMessages).reduce((messages, key) => {
    const value = nestedMessages[key];
    const prefixedKey = prefix ? `${prefix}.${key}` : key;

    if (typeof value === 'string') {
      messages[prefixedKey] = value;
    } else {
      Object.assign(messages, flattenJSONMessages(value, prefixedKey));
    }

    return messages;
  }, {});
}

async function loadMessages(locale) {
  try {
    const [common, learn] = await Promise.all([
      import(`../translations/${locale}/common.json`),
      import(`../translations/${locale}/learn.json`),
    ]);

    const mergedTranslations = {
      ...common.default,
      ...learn.default,
    };

    return flattenJSONMessages(mergedTranslations);
  } catch (error) {
    console.error(`Error loading translations for ${locale}:`, error);
    if (locale !== 'en') {
      return loadMessages('en');
    }
    return {};
  }
}

const App = () => {
  ImportScript('https://www.google.com/recaptcha/api.js');
  const language = localStorage.getItem('hy-language');

  const [emulatorStarted, setEmulatorStarted] = useState(false);
  const [locale, setLocale] = useState(
    language && language !== 'null' && language !== 'undefined'
      ? language
      : 'en',
  );
  const [messages, setMessages] = useState(null);

  useEffect(() => {
    loadMessages(locale).then(setMessages);
  }, [locale]);

  const changeLanguage = (value) => {
    setLocale(value);
    localStorage.setItem('hy-language', value);
  };

  return (
    <AuthContextProvider>
      <Provider store={store}>
        <IntlProvider locale={locale} messages={messages}>
          <Router>
            <Routes>
              <Route path='/test/playground' element={<Playground />} replace />
              <Route path='/action' element={<Action />} replace />
              <Route path='/auth' element={<Auth />} replace />
              <Route
                path='/test-home'
                element={
                  <AuthenticatedRoute
                    element={<TestHome />}
                    areMessagesLoaded={messages}
                  />
                }
                replace
              />
              <Route
                path='/reset-password'
                element={
                  <ResetPassword
                    lang={locale}
                    changeLanguage={changeLanguage}
                  />
                }
                replace
              />
              <Route path='/error' element={<Error />} replace />
              <Route
                path='/phone-registration'
                element={
                  <PhoneRegistration
                    lang={locale}
                    changeLanguage={changeLanguage}
                  />
                }
                replace
              />
              <Route
                path='/authorization'
                element={
                  <SecureRoute
                    areMessagesLoaded={messages}
                    element={
                      <Authorization
                        emulatorStarted={emulatorStarted}
                        setEmulatorStarted={setEmulatorStarted}
                        lang={locale}
                        changeLanguage={changeLanguage}
                      />
                    }
                  />
                }
                replace
              />
              <Route
                path='/log-in'
                element={
                  <SecureRoute
                    areMessagesLoaded={messages}
                    element={
                      <Authorization
                        emulatorStarted={emulatorStarted}
                        setEmulatorStarted={setEmulatorStarted}
                        lang={locale}
                        changeLanguage={changeLanguage}
                      />
                    }
                  />
                }
                replace
              />
              <Route
                path='/mytests'
                element={<Navigate to='/myhealth' replace />}
              />
              <Route
                path='/learn/:topic'
                element={
                  <AlwaysAccessibleRoute
                    element={
                      <LearnMorePost
                        lang={locale}
                        changeLanguage={changeLanguage}
                      />
                    }
                    lang={locale}
                    changeLanguage={changeLanguage}
                    areMessagesLoaded={messages}
                  />
                }
                replace
              />
              <Route
                path='/myhealth'
                element={
                  <AuthenticatedRoute
                    areMessagesLoaded={messages}
                    element={
                      <MyHealth lang={locale} changeLanguage={changeLanguage} />
                    }
                  />
                }
                replace
              />
              <Route
                path='/myhealth/products'
                element={
                  <AuthenticatedRoute
                    areMessagesLoaded={messages}
                    element={
                      <MyHealthProducts
                        lang={locale}
                        changeLanguage={changeLanguage}
                      />
                    }
                  />
                }
                replace
              />
              <Route
                path='/myhealth/employee-health'
                element={
                  <AuthenticatedRoute
                    areMessagesLoaded={messages}
                    element={
                      <MyHealthEmployeeHealth
                        lang={locale}
                        changeLanguage={changeLanguage}
                      />
                    }
                  />
                }
                replace
              />
              <Route
                path='/myhealth/check-up'
                element={
                  <AuthenticatedRoute
                    areMessagesLoaded={messages}
                    element={
                      <MyHealthCheckups
                        lang={locale}
                        changeLanguage={changeLanguage}
                      />
                    }
                  />
                }
                replace
              />
              <Route
                path='/myhealth/shop'
                element={
                  <AuthenticatedRoute
                    areMessagesLoaded={messages}
                    element={
                      <MyHealthShop
                        lang={locale}
                        changeLanguage={changeLanguage}
                      />
                    }
                  />
                }
                replace
              />
              <Route
                path='/test-results/:testCode'
                element={
                  <AuthenticatedRoute
                    areMessagesLoaded={messages}
                    element={
                      <TestResults
                        lang={locale}
                        changeLanguage={changeLanguage}
                      />
                    }
                  />
                }
                replace
              />
              <Route
                path='/test-results'
                element={<Navigate to='/error' replace />}
              />
              <Route
                path='/product-page/treatment/:testCode'
                element={
                  <AuthenticatedRoute
                    areMessagesLoaded={messages}
                    element={
                      <ProductPage
                        lang={locale}
                        changeLanguage={changeLanguage}
                      />
                    }
                  />
                }
                replace
              />
              <Route
                path='/product-page/treatment/:testCode/checkout'
                element={
                  <AuthenticatedRoute
                    areMessagesLoaded={messages}
                    element={
                      <ProductCheckoutPage
                        lang={locale}
                        changeLanguage={changeLanguage}
                      />
                    }
                  />
                }
                replace
              />
              <Route
                path='/treatment/:treatmentId'
                element={
                  <AuthenticatedRoute
                    areMessagesLoaded={messages}
                    element={
                      <TreatmentPage
                        lang={locale}
                        changeLanguage={changeLanguage}
                      />
                    }
                  />
                }
                replace
              />
              <Route
                path='/:conversationId?'
                element={
                  <Home
                    language={locale}
                    changeLanguage={changeLanguage}
                    areMessagesLoaded={messages}
                  />
                }
                replace
              />
              <Route path='/' element={<Navigate to='/myhealth' replace />} />
            </Routes>
          </Router>
        </IntlProvider>
      </Provider>
    </AuthContextProvider>
  );
};

export default App;
